// Browser-injectable function for inferring an element's applied CSS styles
// as an inline-style string, preserving var()/calc() and expanding shorthands.

import {MDN_SHORTHAND_MAP} from './generated/shorthand-map';
import {MDN_INHERITABLE_PROPERTIES} from './generated/inheritable-properties';

type ShorthandMap = Record<string, string[]>;

interface MatchedRule {
  rule: CSSStyleRule;
  layer: string | null;
  sourceIndex: number;
  sheet: CSSStyleSheet;
}

// ── Shorthand → longhand mapping ────────────────────────────────────────
// The base map is generated from mdn-data (see scripts/generate-shorthand-map.ts).
// Chrome expands some shorthands to additional sub-properties that mdn-data
// doesn't track (e.g. `font` resets `font-variant-caps`, `font-kerning`, etc.).
// We supplement the generated map with these browser-specific expansions so
// the noise filter can identify them as shorthand side-effects.
const BROWSER_SHORTHAND_SUPPLEMENTS: ShorthandMap = {
  font: [
    'font-variant-caps',
    'font-variant-ligatures',
    'font-variant-numeric',
    'font-variant-east-asian',
    'font-variant-alternates',
    'font-variant-position',
    'font-variant-emoji',
    'font-size-adjust',
    'font-language-override',
    'font-kerning',
    'font-optical-sizing',
    'font-feature-settings',
    'font-variation-settings',
  ],
};

const SHORTHAND_MAP: ShorthandMap = {};
for (const [key, value] of Object.entries(MDN_SHORTHAND_MAP)) {
  const supplements = BROWSER_SHORTHAND_SUPPLEMENTS[key];
  SHORTHAND_MAP[key] = supplements ? [...value, ...supplements] : value;
}

// Reverse mapping: longhand → possible parent shorthands
const LONGHAND_TO_SHORTHANDS: Record<string, string[]> = {};
for (const sh of Object.keys(SHORTHAND_MAP)) {
  for (const lh of SHORTHAND_MAP[sh]!) {
    if (!LONGHAND_TO_SHORTHANDS[lh]) LONGHAND_TO_SHORTHANDS[lh] = [];
    LONGHAND_TO_SHORTHANDS[lh]!.push(sh);
  }
}

// Edge-property shorthands (1/2/3/4 value distribution)
const EDGE_SHORTHANDS: ShorthandMap = {
  margin: ['margin-top', 'margin-right', 'margin-bottom', 'margin-left'],
  padding: ['padding-top', 'padding-right', 'padding-bottom', 'padding-left'],
  'border-width': [
    'border-top-width',
    'border-right-width',
    'border-bottom-width',
    'border-left-width',
  ],
  'border-style': [
    'border-top-style',
    'border-right-style',
    'border-bottom-style',
    'border-left-style',
  ],
  'border-color': [
    'border-top-color',
    'border-right-color',
    'border-bottom-color',
    'border-left-color',
  ],
  'border-radius': [
    'border-top-left-radius',
    'border-top-right-radius',
    'border-bottom-right-radius',
    'border-bottom-left-radius',
  ],
  inset: ['top', 'right', 'bottom', 'left'],
  'scroll-margin': [
    'scroll-margin-top',
    'scroll-margin-right',
    'scroll-margin-bottom',
    'scroll-margin-left',
  ],
  'scroll-padding': [
    'scroll-padding-top',
    'scroll-padding-right',
    'scroll-padding-bottom',
    'scroll-padding-left',
  ],
};

const TWO_VALUE_SHORTHANDS: ShorthandMap = {
  overflow: ['overflow-x', 'overflow-y'],
  gap: ['row-gap', 'column-gap'],
  'place-content': ['align-content', 'justify-content'],
  'place-items': ['align-items', 'justify-items'],
  'place-self': ['align-self', 'justify-self'],
  'margin-block': ['margin-block-start', 'margin-block-end'],
  'margin-inline': ['margin-inline-start', 'margin-inline-end'],
  'padding-block': ['padding-block-start', 'padding-block-end'],
  'padding-inline': ['padding-inline-start', 'padding-inline-end'],
  'inset-block': ['inset-block-start', 'inset-block-end'],
  'inset-inline': ['inset-inline-start', 'inset-inline-end'],
};

// ── CSS value tokenizer ─────────────────────────────────────────────────
// Splits a CSS value on whitespace while respecting nested parentheses,
// brackets, and quoted strings.
function splitCSSValue(value: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let depth = 0;
  let inString = false;
  let stringChar = '';

  for (let ci = 0; ci < value.length; ci++) {
    const ch = value[ci]!;
    if (inString) {
      current += ch;
      if (ch === stringChar) inString = false;
      continue;
    }
    if (ch === '"' || ch === "'") {
      inString = true;
      stringChar = ch;
      current += ch;
      continue;
    }
    if (ch === '(' || ch === '[') {
      depth++;
      current += ch;
    } else if (ch === ')' || ch === ']') {
      depth--;
      current += ch;
    } else if ((ch === ' ' || ch === '\t' || ch === '\n') && depth === 0) {
      if (current.trim()) tokens.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) tokens.push(current.trim());
  return tokens;
}

// ── Shorthand value expansion ───────────────────────────────────────────

function expandEdgeValues(tokens: string[]): string[] {
  switch (tokens.length) {
    case 1:
      return [tokens[0]!, tokens[0]!, tokens[0]!, tokens[0]!];
    case 2:
      return [tokens[0]!, tokens[1]!, tokens[0]!, tokens[1]!];
    case 3:
      return [tokens[0]!, tokens[1]!, tokens[2]!, tokens[1]!];
    default:
      return [tokens[0]!, tokens[1]!, tokens[2]!, tokens[3]!];
  }
}

function expandTwoValues(tokens: string[]): [string, string] {
  if (tokens.length >= 2) return [tokens[0]!, tokens[1]!];
  return [tokens[0]!, tokens[0]!];
}

function expandShorthandValue(
  shorthand: string,
  value: string
): Record<string, string> | null {
  const tokens = splitCSSValue(value);
  if (tokens.length === 0) return null;

  if (shorthand in EDGE_SHORTHANDS) {
    const longhands = EDGE_SHORTHANDS[shorthand]!;
    const expanded = expandEdgeValues(tokens);
    const result: Record<string, string> = {};
    for (let i = 0; i < longhands.length; i++) {
      result[longhands[i]!] = expanded[i] ?? tokens[0]!;
    }
    return result;
  }

  if (shorthand in TWO_VALUE_SHORTHANDS) {
    const twoLonghands = TWO_VALUE_SHORTHANDS[shorthand]!;
    const [first, second] = expandTwoValues(tokens);
    return {[twoLonghands[0]!]: first, [twoLonghands[1]!]: second};
  }

  return null;
}

// ── Source text recovery ────────────────────────────────────────────────
// Chrome discards shorthand values when a longhand override exists in the
// same rule (e.g., `margin: var(--sp); margin-top: var(--top)` loses the
// shorthand). We recover the original shorthand from stylesheet source text.
//
// Known limitations: may mis-match when multiple rules share the same
// selector text, or when CSS is minified. Returns null for external and
// programmatically created sheets (no ownerNode / textContent).
function getOriginalDeclarations(
  sheet: CSSStyleSheet,
  rule: CSSStyleRule
): Array<{property: string; value: string}> | null {
  const ownerNode = sheet.ownerNode as HTMLElement | null;
  if (!ownerNode || !ownerNode.textContent) return null;
  const sourceText = ownerNode.textContent;
  const selector = rule.selectorText;

  const selectorIdx = sourceText.indexOf(selector);
  if (selectorIdx === -1) return null;

  const braceStart = sourceText.indexOf('{', selectorIdx);
  if (braceStart === -1) return null;

  let depth = 1;
  let braceEnd = braceStart + 1;
  while (braceEnd < sourceText.length && depth > 0) {
    if (sourceText[braceEnd] === '{') depth++;
    if (sourceText[braceEnd] === '}') depth--;
    braceEnd++;
  }

  const blockText = sourceText.slice(braceStart + 1, braceEnd - 1).trim();

  const declarations: Array<{property: string; value: string}> = [];
  let parenDepth = 0;
  let current = '';
  for (let ci = 0; ci < blockText.length; ci++) {
    const ch = blockText[ci]!;
    if (ch === '(') parenDepth++;
    if (ch === ')') parenDepth--;
    if (ch === ';' && parenDepth === 0) {
      if (current.trim()) {
        const colonIdx = current.indexOf(':');
        if (colonIdx !== -1) {
          const prop = current.slice(0, colonIdx).trim();
          let val = current.slice(colonIdx + 1).trim();
          val = val.replace(/\/\*[^]*?\*\//g, '').trim();
          declarations.push({property: prop, value: val});
        }
      }
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) {
    const colonIdx = current.indexOf(':');
    if (colonIdx !== -1) {
      const prop = current.slice(0, colonIdx).trim();
      let val = current.slice(colonIdx + 1).trim();
      val = val.replace(/\/\*[^]*?\*\//g, '').trim();
      declarations.push({property: prop, value: val});
    }
  }

  return declarations;
}

// ── CSSOM walker ────────────────────────────────────────────────────────
function getAllMatchingRules(el: Element): MatchedRule[] {
  const doc = el.ownerDocument;
  const matched: MatchedRule[] = [];
  let globalIndex = 0;

  function walkRules(
    ruleList: CSSRuleList,
    layer: string | null,
    sheet: CSSStyleSheet
  ) {
    for (let ri = 0; ri < ruleList.length; ri++) {
      const rule = ruleList[ri]!;

      if (rule instanceof CSSStyleRule) {
        try {
          if (el.matches(rule.selectorText)) {
            matched.push({rule, layer, sourceIndex: globalIndex, sheet});
          }
        } catch {
          // Invalid selectors can throw on .matches()
        }
      } else if (rule instanceof CSSMediaRule) {
        if (matchMedia(rule.conditionText).matches) {
          walkRules(rule.cssRules, layer, sheet);
        }
      } else if (rule instanceof CSSSupportsRule) {
        if (CSS.supports(rule.conditionText)) {
          walkRules(rule.cssRules, layer, sheet);
        }
      } else if (
        typeof CSSLayerBlockRule !== 'undefined' &&
        rule instanceof CSSLayerBlockRule
      ) {
        const layerName = layer ? `${layer}.${rule.name}` : rule.name;
        walkRules(rule.cssRules, layerName, sheet);
      } else if ('cssRules' in rule && (rule as CSSGroupingRule).cssRules) {
        walkRules((rule as CSSGroupingRule).cssRules, layer, sheet);
      }

      globalIndex++;
    }
  }

  for (let si = 0; si < doc.styleSheets.length; si++) {
    const sheet = doc.styleSheets[si]!;
    try {
      walkRules(sheet.cssRules, null, sheet);
    } catch {
      // Cross-origin sheets throw SecurityError
    }
  }

  if (doc.adoptedStyleSheets) {
    for (const sheet of doc.adoptedStyleSheets) {
      try {
        walkRules(sheet.cssRules, null, sheet);
      } catch {
        // guard
      }
    }
  }

  // Shadow DOM: walk stylesheets scoped to the element's shadow root
  const root = el.getRootNode();
  if (root instanceof ShadowRoot) {
    const shadowStyles = root.querySelectorAll('style');
    for (const styleEl of shadowStyles) {
      if (styleEl.sheet) {
        try {
          walkRules(styleEl.sheet.cssRules, null, styleEl.sheet);
        } catch {
          // guard
        }
      }
    }
    if (root.adoptedStyleSheets) {
      for (const sheet of root.adoptedStyleSheets) {
        try {
          walkRules(sheet.cssRules, null, sheet);
        } catch {
          // guard
        }
      }
    }
  }

  return matched;
}

// ── Parent shorthand finder ─────────────────────────────────────────────
function findParentShorthand(
  style: CSSStyleDeclaration,
  longhand: string
): string | null {
  const candidates = LONGHAND_TO_SHORTHANDS[longhand];
  if (!candidates) return null;
  let best: string | null = null;
  let bestLen = Infinity;
  for (const shName of candidates) {
    const val = style.getPropertyValue(shName);
    if (val) {
      const len = SHORTHAND_MAP[shName]?.length ?? Infinity;
      if (len < bestLen) {
        best = shName;
        bestLen = len;
      }
    }
  }
  return best;
}

function isExpandedFromShorthand(
  style: CSSStyleDeclaration,
  prop: string
): boolean {
  const candidates = LONGHAND_TO_SHORTHANDS[prop];
  if (!candidates) return false;
  for (const sh of candidates) {
    if (style.getPropertyValue(sh)) return true;
  }
  return false;
}

// ── Removal-probe ───────────────────────────────────────────────────────
// Temporarily removes a declaration and checks if the computed value
// changes. Uses cssText save/restore to avoid CSSOM corruption.
function probeRemove(
  style: CSSStyleDeclaration,
  prop: string,
  el: HTMLElement,
  targetProp: string,
  computedVal: string
): boolean {
  const savedCssText = style.cssText;
  style.removeProperty(prop);
  const newVal = getComputedStyle(el).getPropertyValue(targetProp);
  style.cssText = savedCssText;
  return newVal !== computedVal;
}

// ── Probe for value ─────────────────────────────────────────────────────
function probeForValue(
  el: HTMLElement,
  prop: string,
  matchingRules: MatchedRule[],
  cleanComputed: CSSStyleDeclaration
): string | null {
  const computedVal = getComputedStyle(el).getPropertyValue(prop);

  // --- Inline style (longhand) ---
  const inlineVal = el.style.getPropertyValue(prop);
  if (inlineVal) {
    if (probeRemove(el.style, prop, el, prop, computedVal)) {
      return inlineVal;
    }
  }

  // --- Inline style (shorthand containing this longhand) ---
  const inlineShorthand = findParentShorthand(el.style, prop);
  if (inlineShorthand) {
    const inlineShortVal = el.style.getPropertyValue(inlineShorthand);
    if (
      inlineShortVal &&
      probeRemove(el.style, inlineShorthand, el, prop, computedVal)
    ) {
      const inlineExpanded = expandShorthandValue(
        inlineShorthand,
        inlineShortVal
      );
      if (inlineExpanded && prop in inlineExpanded)
        return inlineExpanded[prop]!;
      return inlineShortVal;
    }
  }

  // --- Stylesheet rules (reverse source order) ---
  for (let ri = matchingRules.length - 1; ri >= 0; ri--) {
    const {rule} = matchingRules[ri]!;

    // Direct longhand
    const ruleVal = rule.style.getPropertyValue(prop);
    if (ruleVal) {
      if (probeRemove(rule.style, prop, el, prop, computedVal)) {
        return ruleVal;
      }
    }

    // Parent shorthand (var() case — browser stores the shorthand name)
    const parentShorthand = findParentShorthand(rule.style, prop);
    if (parentShorthand) {
      const shortVal = rule.style.getPropertyValue(parentShorthand);
      if (
        shortVal &&
        probeRemove(rule.style, parentShorthand, el, prop, computedVal)
      ) {
        const expanded = expandShorthandValue(parentShorthand, shortVal);
        if (expanded && prop in expanded) return expanded[prop]!;
        return shortVal;
      }
    }
  }

  // --- Fallback: probe didn't detect a change ───────────────────────
  // This happens when the authored value equals the browser default.

  // Inline (directly declared) — always include
  if (inlineVal) return inlineVal;

  // Stylesheet rules
  for (let fi = matchingRules.length - 1; fi >= 0; fi--) {
    const fbMatch = matchingRules[fi]!;
    const fbRule = fbMatch.rule;

    const fbVal = fbRule.style.getPropertyValue(prop);
    if (fbVal) {
      // If expanded from a shorthand, only include if computed differs
      // from the clean element (filters noise like border-image-*)
      if (isExpandedFromShorthand(fbRule.style, prop)) {
        if (computedVal !== cleanComputed.getPropertyValue(prop)) {
          return fbVal;
        }
        continue;
      }
      return fbVal;
    }

    // Parent shorthand with a value
    const fbShorthand = findParentShorthand(fbRule.style, prop);
    if (fbShorthand) {
      const fbShortVal = fbRule.style.getPropertyValue(fbShorthand);
      if (fbShortVal) {
        const fbExpanded = expandShorthandValue(fbShorthand, fbShortVal);
        if (fbExpanded && prop in fbExpanded) return fbExpanded[prop]!;
        return fbShortVal;
      }
    }

    // Empty-value longhand from a var()-containing shorthand that was
    // partially overridden within the same rule. Recover the original
    // shorthand from the stylesheet source text.
    let hasEmptyInRule = false;
    for (let ei = 0; ei < fbRule.style.length; ei++) {
      if (
        fbRule.style.item(ei) === prop &&
        !fbRule.style.getPropertyValue(prop)
      ) {
        hasEmptyInRule = true;
        break;
      }
    }
    if (hasEmptyInRule) {
      const origDecls = getOriginalDeclarations(fbMatch.sheet, fbRule);
      if (origDecls) {
        for (let di = origDecls.length - 1; di >= 0; di--) {
          const decl = origDecls[di]!;
          if (decl.property === prop && decl.value) {
            return decl.value;
          }
          const declLonghands = SHORTHAND_MAP[decl.property];
          if (declLonghands?.includes(prop) && decl.value) {
            const declExpanded = expandShorthandValue(
              decl.property,
              decl.value
            );
            if (declExpanded && prop in declExpanded)
              return declExpanded[prop]!;
            return decl.value;
          }
        }
      }
    }
  }

  return null;
}

// ── Inherited property support ──────────────────────────────────────────
// CSS properties that inherit by default. Derived from mdn-data (see
// scripts/generate-shorthand-map.ts). When `includeInherited` is enabled,
// the ancestor walk probes these on parent elements to recover authored
// values (var(), named colors, relative units).
const INHERITABLE_PROPERTIES = MDN_INHERITABLE_PROPERTIES;

const MAX_ANCESTOR_DEPTH = 50;

interface GetInferredStylesOptions {
  /**
   * When true, walk the ancestor chain to recover authored values for
   * inheritable CSS properties not found in the element's own rules.
   * Falls back to getComputedStyle for any property the walk cannot resolve.
   */
  includeInherited?: boolean;
  /**
   * CSS longhand property names to backfill from getComputedStyle when the
   * probe-based inference didn't discover them (e.g. properties set only by
   * the user-agent stylesheet which is not accessible via CSSOM).  Only
   * properties whose computed value differs from a clean same-tag element
   * are included, so browser defaults that match are filtered out.
   */
  backfillProperties?: string[];
}

// ── Main function ───────────────────────────────────────────────────────
/**
 * Infers applied CSS styles as a semicolon-separated inline-style string,
 * preserving var()/calc() and expanding shorthands. Uses removal-probe
 * cascade resolution -- call once per selection, not per frame.
 */
export function getInferredStyles(
  element: HTMLElement,
  options?: GetInferredStylesOptions
): string {
  const doc = element.ownerDocument;
  const matchingRules = getAllMatchingRules(element);

  // Reference element for default-value comparison. Placed in the same root
  // as the target so shadow-scoped styles are reflected. For non-shadow DOM,
  // appended to <body> to avoid perturbing :nth-child on the target.
  const cleanEl = doc.createElement(element.tagName);
  cleanEl.style.display = 'none';
  const elementRoot = element.getRootNode();
  if (elementRoot instanceof ShadowRoot) {
    elementRoot.appendChild(cleanEl);
  } else {
    doc.body.appendChild(cleanEl);
  }
  const cleanComputed = getComputedStyle(cleanEl);

  // Step 1: Collect all target longhand properties
  const targetProps = new Set<string>();

  function collectProps(style: CSSStyleDeclaration) {
    for (let pi = 0; pi < style.length; pi++) {
      const prop = style.item(pi);
      const val = style.getPropertyValue(prop);
      if (val) {
        const longhands = SHORTHAND_MAP[prop];
        if (longhands) {
          for (const lh of longhands) targetProps.add(lh);
        } else {
          targetProps.add(prop);
        }
      } else {
        targetProps.add(prop);
      }
    }
  }

  for (const mr of matchingRules) collectProps(mr.rule.style);
  collectProps(element.style);

  // Step 2: For each target longhand, find the cascade-winning authored value
  const result: Record<string, string> = {};

  for (const targetProp of targetProps) {
    const value = probeForValue(
      element,
      targetProp,
      matchingRules,
      cleanComputed
    );
    if (value) {
      result[targetProp] = value;
    }
  }

  // Step 2.5: Recover authored inherited values via ancestor walk
  if (options?.includeInherited) {
    const missing = INHERITABLE_PROPERTIES.filter((p) => !result[p]);

    if (missing.length > 0) {
      const remaining = new Set(missing);
      // Properties for which the ancestor walk found an authored value but
      // skipped it because applying it inline on the child would resolve
      // against a different base (e.g. font-size: 1.5em). These get the
      // computed value as a fallback so round-tripping still works.
      const skippedRelative = new Set<string>();
      let current: Element | null = element.parentElement;

      if (!current) {
        const root = element.getRootNode();
        if (root instanceof ShadowRoot && root.host) {
          current = root.host;
        }
      }

      let depth = 0;

      while (current && remaining.size > 0 && depth < MAX_ANCESTOR_DEPTH) {
        try {
          const ancestorRules = getAllMatchingRules(current);

          for (const prop of [...remaining]) {
            try {
              const value = probeForValue(
                current as HTMLElement,
                prop,
                ancestorRules,
                cleanComputed
              );
              if (value) {
                if (
                  prop === 'font-size' &&
                  /(?:^|[^a-z])(\d*\.?\d+)\s*(em|ex|ch|%)/.test(value)
                ) {
                  skippedRelative.add(prop);
                  continue;
                }
                result[prop] = value;
                remaining.delete(prop);
              }
            } catch {
              // Single property probe failed — skip, try next ancestor
            }
          }
        } catch {
          // getAllMatchingRules failed on this ancestor — skip it
        }

        const next = current.parentElement;
        if (next) {
          current = next;
        } else {
          const root = current.getRootNode();
          current = root instanceof ShadowRoot && root.host ? root.host : null;
        }
        depth++;
      }

      // Fallback: only resolve relative-unit properties that the walk
      // intentionally skipped. Properties for which no ancestor had an
      // authored value are left out — their computed values reflect browser
      // defaults (e.g. caret-color: auto resolving to currentColor) rather
      // than authored intent.
      if (skippedRelative.size > 0) {
        const computed = getComputedStyle(element);
        for (const prop of skippedRelative) {
          if (result[prop]) continue;
          const val = computed.getPropertyValue(prop);
          const cleanVal = cleanComputed.getPropertyValue(prop);
          if (val && val !== cleanVal) {
            result[prop] = val;
          }
        }
      }
    }
  }

  // Step 2.75: Backfill caller-specified properties from computed style.
  // Catches values originating from sources the CSSOM walk can't see (e.g.
  // user-agent stylesheets) by comparing against the clean element baseline.
  if (options?.backfillProperties?.length) {
    const computed = getComputedStyle(element);
    for (const prop of options.backfillProperties) {
      if (result[prop]) continue;
      const val = computed.getPropertyValue(prop);
      const cleanVal = cleanComputed.getPropertyValue(prop);
      if (val && val !== cleanVal) {
        result[prop] = val;
      }
    }
  }

  // Clean up
  cleanEl.remove();

  // Step 3: Format as inline-style string
  return Object.keys(result)
    .sort()
    .map((k) => `${k}: ${result[k]}`)
    .join('; ');
}
